import { useCallback, useEffect, useRef, useState } from "react";
import { DICTATION_MAX_SECONDS } from "@pushback-and-chaos/contracts";

/**
 * The microphone (ADR 0011), as a hook the input owns.
 *
 * Press to start, press again to finish; the recording goes out through the
 * one function the caller hands in, and the words come back to land in the
 * draft. Nothing here knows a URL — `transcribe` is packages/client's method,
 * passed down — and nothing here is ever sent as a turn: the learner reads
 * what was heard and changes it before they send. A mishearing is a typo,
 * never a day of the engagement.
 *
 * The permission prompt is the browser's own, raised the first time the
 * learner presses the control and never before. A refusal is remembered as a
 * state the control can say out loud, not as an error.
 */
/** `asking` is the browser's own permission prompt being open — the control says so, because a prompt can be behind another window or, in some embedded browsers, never appear at all. */
export type DictationState = "idle" | "asking" | "listening" | "transcribing" | "denied" | "failed";

export type TranscribeFn = (audio: Blob, durationSeconds: number) => Promise<string>;

export interface DictationControl {
  readonly state: DictationState;
  /** False when this browser has no recorder or no microphone API at all — the control shows as "not yet." then. */
  readonly supported: boolean;
  /** Start listening, or finish and send the recording, depending on state. */
  readonly toggle: () => void;
}

/** The containers a MediaRecorder can produce, in the order the server prefers them. Chrome and Firefox say webm; Safari says mp4. */
const PREFERRED_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"] as const;

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return undefined;
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

function isSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices !== "undefined" &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

export function useDictation(
  transcribe: TranscribeFn | null | undefined,
  onText: (text: string) => void,
  options: { readonly maxSeconds?: number } = {},
): DictationControl {
  const [state, setState] = useState<DictationState>("idle");
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startedAt = useRef<number>(0);
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOnText = useRef(onText);
  latestOnText.current = onText;
  const latestTranscribe = useRef(transcribe);
  latestTranscribe.current = transcribe;
  const maxSeconds = options.maxSeconds ?? DICTATION_MAX_SECONDS;

  const releaseMicrophone = useCallback(() => {
    for (const track of stream.current?.getTracks() ?? []) track.stop();
    stream.current = null;
    recorder.current = null;
    if (stopTimer.current !== null) {
      clearTimeout(stopTimer.current);
      stopTimer.current = null;
    }
  }, []);

  const finish = useCallback(() => {
    const active = recorder.current;
    if (active === null || active.state === "inactive") return;
    active.stop();
  }, []);

  const start = useCallback(async () => {
    const send = latestTranscribe.current;
    if (send === null || send === undefined || !isSupported()) return;
    let media: MediaStream;
    setState("asking");
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      setState(error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError") ? "denied" : "failed");
      return;
    }
    const mimeType = pickMimeType();
    const active = new MediaRecorder(media, mimeType !== undefined ? { mimeType } : {});
    stream.current = media;
    recorder.current = active;
    chunks.current = [];
    startedAt.current = Date.now();

    active.addEventListener("dataavailable", (event: BlobEvent) => {
      if (event.data.size > 0) chunks.current.push(event.data);
    });
    active.addEventListener("stop", () => {
      const durationSeconds = Math.max(1, Math.round((Date.now() - startedAt.current) / 1000));
      const audio = new Blob(chunks.current, { type: active.mimeType.length > 0 ? active.mimeType : (mimeType ?? "audio/webm") });
      releaseMicrophone();
      if (audio.size === 0) {
        setState("idle");
        return;
      }
      setState("transcribing");
      send(audio, durationSeconds)
        .then((text) => {
          if (text.trim().length > 0) latestOnText.current(text.trim());
          setState("idle");
        })
        .catch(() => {
          setState("failed");
        });
    });

    active.start();
    setState("listening");
    stopTimer.current = setTimeout(finish, maxSeconds * 1000);
  }, [finish, maxSeconds, releaseMicrophone]);

  const toggle = useCallback(() => {
    switch (state) {
      case "listening":
        finish();
        return;
      case "asking":
      case "transcribing":
        return;
      case "idle":
      case "denied":
      case "failed":
        void start();
        return;
    }
  }, [state, finish, start]);

  // Leaving the surface mid-recording must not leave the microphone light on.
  useEffect(() => releaseMicrophone, [releaseMicrophone]);

  return { state, supported: isSupported(), toggle };
}
